/**
 * Write-up monitoring pages (read-only).
 *   Approvals monitor  GET v3/asks?scope=admin&kind=loan-approval&status=…  (scope=admin is required: scope=all is silently the
 *                      caller's OWN asks - the 10/16-11/02 miscount that read "no approvals" while 31 existed)
 *   Scheduled jobs     GET v3/loans/admin/detectors  (each detector's mode, last run and what it has queued)
 *                      PUT v3/loans/admin/detectors/{detector}/mode  (LOP, Bill 11/03: "Change mode" for admins whose capability answer
 *                      holds monitoring.jobs.mode; a modal with the mode and the change reason, audited by the API)
 * Ports of the LWP admin panel's Approval monitor and Scheduled jobs; the LWP screens come out once these are live (§7).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Banner, Button, Pill, Spinner, type PillTone } from '@/components/primitives';
import { DataTable } from '@/components/DataTable';
import { SelectField, TextField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';
import { ReasonField, asciiReason, reasonProblem } from '@/components/ChangeReason';
import { usePlatforms } from '@/contexts/PlatformsContext';
import { loansApi } from '../../loansApi';
import { modalError } from '../../adminKit';
import { errorText, fmtCt, lwpLoanLink } from '../common';

// ─── Approvals monitor ───────────────────────────────────────────────────

interface LoanAsk {
  id: number;
  subject: string;
  status: string;
  token: string;
  loanItemId: number | null;
  requestedBy: string;
  requestedAt: string | null;
  assignees: string[];
  dueDate: string | null;
  lastNudgedAt: string | null;
  settledAt: string | null;
  settledBy: string | null;
  settledOutcome: string | null;
  settledChannel: string | null;
  settledComment: string | null;
  cardsDelivered: number | null;
  cardsFailed: number | null;
  supersededByAskId: number | null;
}
interface AsksAnswer { count: number; items: LoanAsk[] }

const STATUS = [
  { value: 'Open', label: 'Open' },
  { value: 'Settled', label: 'Settled' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: '', label: 'All' },
];

const outcomeTone = (a: LoanAsk): PillTone =>
  a.status === 'Open' ? 'warn' : a.status === 'Cancelled' ? 'muted' : /approv/i.test(a.settledOutcome ?? '') ? 'ok' : /reject|declin/i.test(a.settledOutcome ?? '') ? 'err' : 'info';

const loanIdOf = (a: LoanAsk): number | null => a.loanItemId ?? (/^WU-(\d+)/i.exec(a.token ?? '') ? Number(/^WU-(\d+)/i.exec(a.token)![1]) : null);

export function LoansApprovalsPage(): JSX.Element {
  const [status, setStatus] = useState('Open');
  const [text, setText] = useState('');
  const [items, setItems] = useState<LoanAsk[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await loansApi.get<AsksAnswer>('v3/asks', { scope: 'admin', kind: 'loan-approval', status: status || undefined, top: 500 });
      setItems(r.items ?? []);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => {
    const t = text.trim().toLowerCase();
    if (!t) return items;
    return items.filter((a) => [a.subject, a.token, a.requestedBy, ...(a.assignees ?? []), String(loanIdOf(a) ?? '')].some((v) => (v ?? '').toLowerCase().includes(t)));
  }, [items, text]);

  const failedCards = items.filter((a) => (a.cardsFailed ?? 0) > 0).length;

  return (
    <div>
      <h2>Approvals monitor</h2>
      <p className="page-intro">
        Every loan approval request (CLO, executive and supervisor signatures) across all officers: who asked, who must answer, and how
        it settled. Read-only; an approval is settled from its Teams card or the write-up.
      </p>
      <div className="toolbar">
        <SelectField label="Status" value={status} onChange={setStatus} options={STATUS} />
        <TextField label="Filter" value={text} onChange={setText} placeholder="WU number, borrower, officer…" />
        <Button variant="ghost" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>
      {failedCards > 0 && <Banner tone="warn">{failedCards} request(s) have Teams cards that failed to deliver.</Banner>}
      {error && <Banner tone="err">{error}</Banner>}
      {loading && items.length === 0 && <Spinner />}
      {!error && (
        <DataTable
          rows={shown}
          rowKey={(a) => String(a.id)}
          initialSort={{ columnIndex: 1, dir: 'desc' }}
          emptyState={<div className="empty">No {status ? status.toLowerCase() : ''} loan approval requests.</div>}
          columns={[
            { header: 'Ask', cell: (a) => a.id, sortKey: (a) => a.id },
            { header: 'Asked', cell: (a) => fmtCt(a.requestedAt), sortKey: (a) => a.requestedAt ?? '' },
            {
              header: 'Loan',
              cell: (a) => {
                const id = loanIdOf(a);
                return id ? (
                  <a href={lwpLoanLink(id)} target="_blank" rel="noreferrer">
                    WU-{id}
                  </a>
                ) : (
                  <code>{a.token}</code>
                );
              },
            },
            { header: 'Subject', cell: (a) => a.subject },
            { header: 'Asked by', cell: (a) => a.requestedBy },
            { header: 'Must answer', cell: (a) => (a.assignees ?? []).join(', ') || '—' },
            {
              header: 'Status',
              cell: (a) => (
                <Pill tone={outcomeTone(a)}>
                  {a.status === 'Settled' && a.settledOutcome ? a.settledOutcome : a.status}
                  {a.supersededByAskId ? ` → #${a.supersededByAskId}` : ''}
                </Pill>
              ),
              sortKey: (a) => a.status,
            },
            { header: 'Settled', cell: (a) => (a.settledAt ? `${fmtCt(a.settledAt)} · ${a.settledBy ?? '?'}${a.settledChannel ? ` (${a.settledChannel})` : ''}` : '—') },
            {
              header: 'Cards',
              cell: (a) => (
                <span>
                  {a.cardsDelivered ?? 0} sent{(a.cardsFailed ?? 0) > 0 && <Pill tone="err">{a.cardsFailed} failed</Pill>}
                </span>
              ),
            },
            { header: 'Comment', cell: (a) => <span className="value-clip">{a.settledComment ?? ''}</span> },
          ]}
        />
      )}
    </div>
  );
}

// ─── Scheduled jobs ──────────────────────────────────────────────────────

interface DetectorRun {
  at: string | null;
  status: string | null;
  mode: string | null;
  triggeredBy: string | null;
  error: string | null;
  counts: Record<string, number> | null;
  applied: number | null;
  notApplied: number | null;
}
interface Detector {
  detector: string;
  label: string;
  available: boolean;
  effectiveMode: string;
  panelMode: string;
  ceiling: string;
  changedBy: string | null;
  changedAt: string | null;
  nightlyAt: string | null;
  nextRunAt: string | null;
  lastRun: DetectorRun | null;
  pending: { queuedWrites?: number; undecidedLines?: number } | null;
}

const modeTone = (m: string): PillTone => (m === 'live' ? 'ok' : m === 'dryrun' ? 'info' : m === 'off' ? 'muted' : 'warn');

// LOP (Bill 11/03): changing a detector's mode from the portal.
const MODES = [
  { value: 'off', label: 'Off - does not run' },
  { value: 'dryrun', label: 'Dry run - runs and reports, changes nothing' },
  { value: 'manual', label: 'Manual - runs only when started by hand' },
  { value: 'live', label: 'Live - runs on schedule and applies approved changes' },
];

export function LoansScheduledJobsPage(): JSX.Element {
  const [rows, setRows] = useState<Detector[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { status: platformStatus } = usePlatforms();
  const canChangeMode =
    platformStatus.loans.kind === 'ready' && platformStatus.loans.caps.actions.some((a) => a.id === 'monitoring.jobs.mode');
  const [modeDraft, setModeDraft] = useState<{ d: Detector; mode: string } | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await loansApi.get<{ detectors: Detector[] }>('v3/loans/admin/detectors');
      setRows(r.detectors ?? []);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const closeMode = (): void => {
    if (saving) return;
    setModeDraft(null);
    setSaveError(null);
  };

  const saveMode = async (): Promise<void> => {
    if (!modeDraft) return;
    setTried(true);
    if (modeDraft.mode === (modeDraft.d.panelMode ?? modeDraft.d.effectiveMode)) {
      setSaveError('Nothing has changed.');
      return;
    }
    if (reasonProblem(reason, { asciiOnly: true })) return;
    setSaving(true);
    setSaveError(null);
    try {
      const why = asciiReason(reason);
      const r = await loansApi.send<{ warning?: string | null }>(
        'PUT',
        `v3/loans/admin/detectors/${encodeURIComponent(modeDraft.d.detector)}/mode`,
        { mode: modeDraft.mode, reason: why },
        why,
      );
      setToast(
        r.warning
          ? { tone: 'warn', text: `${modeDraft.d.label} set to ${modeDraft.mode}, but this job's limit in the app settings keeps it at ${modeDraft.d.ceiling} for now.` }
          : { tone: 'info', text: `${modeDraft.d.label} is now ${modeDraft.mode}.` },
      );
      setModeDraft(null);
      void load();
    } catch (e) {
      setSaveError(modalError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2>Scheduled jobs</h2>
      <p className="page-intro">
        The write-up detectors pcb-loans-api runs on a schedule: what mode each is in (live, dry run, manual or off), when it last ran and
        what it did, and anything waiting on a decision.{' '}
        {canChangeMode
          ? 'Use Change mode to switch a job; the change is recorded with your name and reason.'
          : 'Changing a mode needs a global loans admin grant.'}
      </p>
      <div className="toolbar">
        <Button variant="ghost" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}
      {error && <Banner tone="err">{error}</Banner>}
      {loading && rows.length === 0 && <Spinner />}
      {!error && (
        <DataTable
          rows={rows}
          rowKey={(d) => d.detector}
          emptyState={<div className="empty">No detectors reported.</div>}
          columns={[
            { header: 'Job', cell: (d) => (<span>{d.label}<br /><code>{d.detector}</code></span>) },
            {
              header: 'Mode',
              cell: (d) => (
                <span>
                  <Pill tone={modeTone(d.effectiveMode)}>{d.effectiveMode}</Pill>
                  {d.ceiling && d.ceiling !== d.effectiveMode && <span className="link-list__note"> ceiling {d.ceiling}</span>}
                  {!d.available && <Pill tone="err">unavailable</Pill>}
                </span>
              ),
            },
            { header: 'Changed', cell: (d) => (d.changedAt ? `${fmtCt(d.changedAt)} · ${d.changedBy ?? '?'}` : '—') },
            {
              header: 'Last run',
              cell: (d) =>
                d.lastRun ? (
                  <span>
                    {fmtCt(d.lastRun.at)} <Pill tone={d.lastRun.error ? 'err' : 'ok'}>{d.lastRun.status ?? (d.lastRun.error ? 'error' : 'ok')}</Pill>
                    {d.lastRun.error && <div className="form-field__error">{d.lastRun.error}</div>}
                    {d.lastRun.counts && (
                      <div className="link-list__note">
                        {Object.entries(d.lastRun.counts)
                          .map(([k, v]) => `${k} ${v}`)
                          .join(' · ')}
                      </div>
                    )}
                  </span>
                ) : (
                  'never'
                ),
            },
            { header: 'Next run', cell: (d) => fmtCt(d.nextRunAt ?? d.nightlyAt) },
            {
              header: 'Waiting',
              cell: (d) => {
                const q = d.pending?.queuedWrites ?? 0;
                const u = d.pending?.undecidedLines ?? 0;
                return q || u ? <Pill tone="warn">{[q ? `${q} queued writes` : '', u ? `${u} undecided` : ''].filter(Boolean).join(' · ')}</Pill> : '—';
              },
            },
            {
              header: '',
              cell: (d) =>
                canChangeMode && d.available ? (
                  <Button
                    onClick={() => {
                      setReason('');
                      setTried(false);
                      setSaveError(null);
                      setModeDraft({ d, mode: d.panelMode ?? d.effectiveMode });
                    }}
                  >
                    Change mode
                  </Button>
                ) : null,
            },
          ]}
        />
      )}

      <ModalShell
        open={modeDraft !== null}
        title={`Change mode: ${modeDraft?.d.label ?? ''}`}
        onClose={closeMode}
        width={560}
        actions={
          modeDraft && (
            <>
              <Button onClick={closeMode} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void saveMode()} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {modeDraft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <SelectField
              label="Mode"
              value={modeDraft.mode}
              onChange={(v) => setModeDraft({ ...modeDraft, mode: v })}
              options={MODES}
              hint={
                modeDraft.d.ceiling && modeDraft.d.ceiling !== 'live'
                  ? `This job's limit in the app settings is ${modeDraft.d.ceiling}; a higher mode is saved but does not take effect until that limit is raised.`
                  : `In effect now: ${modeDraft.d.effectiveMode}.`
              }
            />
            <ReasonField value={reason} onChange={setReason} showError={tried} asciiOnly />
          </div>
        )}
      </ModalShell>
    </div>
  );
}
